import React, { useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Animated,
  TouchableOpacity,
  Dimensions,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { SVG_COMPONENTS } from '@/assets/admin';

// Lazy require react-dom for web portal rendering
let ReactDOM: any = null;
let toastRootElement: any = null;

if (Platform.OS === 'web') {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  ReactDOM = require('react-dom');
  
  // Create a dedicated toast container that's always at the end of body
  // This ensures toasts appear above all modals
  const doc = (globalThis as any)?.document;
  if (doc) {
    toastRootElement = doc.getElementById('toast-root');
    if (!toastRootElement) {
      toastRootElement = doc.createElement('div');
      toastRootElement.id = 'toast-root';
      toastRootElement.style.cssText = 'position: fixed; top: 0; left: 0; right: 0; bottom: 0; pointer-events: none; z-index: 2147483647;';
      doc.body.appendChild(toastRootElement);
    }
  }
}

const { width: screenWidth } = Dimensions.get('window');

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading';

export interface ToastProps {
  type: ToastType;
  message: string;
  visible: boolean;
  onHide: () => void;
  autoHide?: boolean;
  duration?: number;
  position?: 'top' | 'bottom';
}

const getToastColors = (type: ToastType) => {
  switch (type) {
    case 'success':
      return {
        backgroundColor: '#4CAF50',
        borderColor: '#45A049',
        iconColor: '#FFFFFF',
        textColor: '#FFFFFF',
      };
    case 'error':
      return {
        backgroundColor: '#F44336',
        borderColor: '#D32F2F',
        iconColor: '#FFFFFF',
        textColor: '#FFFFFF',
      };
    case 'warning':
      return {
        backgroundColor: '#FF9800',
        borderColor: '#F57C00',
        iconColor: '#FFFFFF',
        textColor: '#FFFFFF',
      };
    case 'info':
      return {
        backgroundColor: '#2196F3',
        borderColor: '#1976D2',
        iconColor: '#FFFFFF',
        textColor: '#FFFFFF',
      };
    case 'loading':
      return {
        backgroundColor: '#1976D2',
        borderColor: '#1565C0',
        iconColor: '#FFFFFF',
        textColor: '#FFFFFF',
      };
    default:
      return {
        backgroundColor: '#9E9E9E',
        borderColor: '#757575',
        iconColor: '#FFFFFF',
        textColor: '#FFFFFF',
      };
  }
};

export const Toast: React.FC<ToastProps> = ({
  type,
  message,
  visible,
  onHide,
  autoHide = true,
  duration = 4000,
  position = 'top',
}) => {
  const slideAnim = useRef(new Animated.Value(position === 'top' ? -100 : 100)).current;
  const opacityAnim = useRef(new Animated.Value(0)).current;
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const colors = getToastColors(type);

  useEffect(() => {
    if (visible) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Animate in
      Animated.parallel([
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.timing(opacityAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start();

      // Auto hide if enabled
      if (autoHide) {
        timeoutRef.current = setTimeout(() => {
          hideToast();
        }, duration);
      }
    } else {
      hideToast();
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [visible, autoHide, duration]);

  const hideToast = () => {
    Animated.parallel([
      Animated.timing(slideAnim, {
        toValue: position === 'top' ? -100 : 100,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(opacityAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start(() => {
      onHide();
    });
  };

  const handlePress = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    hideToast();
  };

  if (!visible) {
    return null;
  }

  const toastNode = (
    <SafeAreaView style={[styles.container, position === 'bottom' && styles.containerBottom, Platform.OS === 'web' ? styles.containerWeb : null]} pointerEvents="box-none">
      <View style={styles.toastWrapper}>
        <Animated.View
          style={[
            styles.toast,
            {
              backgroundColor: colors.backgroundColor,
              borderColor: colors.borderColor,
              transform: [{ translateY: slideAnim }],
              opacity: opacityAnim,
            },
          ]}
        >
        <TouchableOpacity
          style={styles.toastContent}
          onPress={handlePress}
          activeOpacity={0.8}
        >
          {type === 'loading' && (
            <View style={styles.iconContainer}>
              <ActivityIndicator size="small" color={colors.iconColor} />
            </View>
          )}
          
          <Text style={[styles.message, { color: colors.textColor }]} numberOfLines={3}>
            {message}
          </Text>
          
          {type !== 'loading' && (
            <TouchableOpacity style={styles.closeButton} onPress={handlePress}>
              <SVG_COMPONENTS.CANCEL_X width={16} height={16} color={colors.textColor} />
            </TouchableOpacity>
          )}
        </TouchableOpacity>
        </Animated.View>
      </View>
    </SafeAreaView>
  );

  if (Platform.OS === 'web' && ReactDOM && toastRootElement) {
    // Render to dedicated toast container that's always at the end of body
    // This ensures toasts appear above all modals
    return ReactDOM.createPortal(toastNode, toastRootElement);
  }

  return toastNode;
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    paddingTop: 10,
    alignItems: 'center', // Center the toast horizontally
  },
  containerWeb: {
    position: 'fixed' as any,
    zIndex: 2147483647, // ensure on top of any overlay
  },
  containerBottom: {
    top: undefined,
    bottom: 0,
    paddingTop: 0,
    paddingBottom: 10,
  },
  toastWrapper: {
    width: '100%',
    maxWidth: Platform.select({
      web: screenWidth > 768 ? 420 : screenWidth - 32, // Responsive width on web
      default: screenWidth - 32, // Full width minus padding on mobile
    }),
    paddingHorizontal: 16,
    ...Platform.select({
      web: {
        // Add some margin for very wide screens
        marginHorizontal: screenWidth > 1200 ? 'auto' : 0,
        // Enable pointer events on the toast wrapper since root has pointer-events: none
        pointerEvents: 'auto' as any,
      },
    }),
  },
  toast: {
    borderRadius: 12,
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  toastContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    minHeight: 56,
  },
  iconContainer: {
    marginRight: 12,
  },
  message: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 20,
  },
  closeButton: {
    marginLeft: 12,
    padding: 4,
  },
});
